import { useEffect, useMemo, useState } from 'react';
import { config } from '../../config';
import type { CoachCardSource, WorkflowEntry } from '../../app/coach';
import { teamProfileFacts } from '../../domain/teamProfile';
import { ARTIFACT_PHASE } from '../../types';
import type { AdjustmentEntry, CoachCardContext, MatchRow } from '../../types';
import { referenceCardB } from '../card/cardB';
import { CoachCardView } from '../card/CoachCardView';
import { PhaseIcon } from '../PhaseIcon';
import { phaseLabelB } from '../phases';
import { AdjustmentSheetB } from './AdjustmentSheetB';
import { HypothesisSheetB } from './HypothesisSheetB';
import { useWorkflowB } from './useWorkflowB';
import {
  canAddEntryB,
  DISCLOSURE_COPY,
  FAILURE_COPY,
  renderableCountB,
  ROSTER_EMPTY_NOTE,
  rosterDisclosureB,
} from './workflowB';

/**
 * B-15 — the **adjustments workflow**, steps 1–3 and the finalize (design spec §2.5; scenarios
 * 22–28, 31). One stepped flow, context-parameterized: the break hub opens it at `halftime`
 * (B-14's entry), and B-18's match record will open the same component at `fullTime` for both
 * purposes. Nothing here reads the purpose except through the workflow's own guards — the card's
 * **framing** (halftime adjustments / full-time review / points of emphasis) is B-16's, along
 * with the paged card itself.
 *
 * ── The manual path is a first-class equal at every step ────────────────────
 *
 * Scenario 27's promise is structural here, not conditional: *Write my own* leaves Step 1
 * without touching the network, every failure code lands in the same Step 3 with the same
 * controls, and `FinalizeCoachCard` is the one exit either way. There is no branch anywhere
 * below that asks whether the AI was called.
 *
 * ── Nothing on this surface fences the whistle ──────────────────────────────
 *
 * §2.5: the second-half whistle stays live in the top band throughout. This surface is the
 * middle band (never a dialog), and both sheets it opens are **non-modal** — a modal would
 * inert the transition control, which is the one control the break may never take away.
 */

export interface AdjustmentsWorkflowBProps {
  /** The SHELL's authoritative match row — re-read after every committed transition. */
  readonly match: MatchRow;
  readonly context: CoachCardContext;
  /** Leave the workflow (the hub returns). The draft persists exactly as it stands. */
  readonly onClose: () => void;
  /**
   * B-31/§21.4 — a one-line notice from the entry action, or `null`. Today it carries exactly one
   * sentence: a live recording was finalized on entry, because §21.4 removes the bottom band —
   * and with it the Flag button, which is a recording's only stop control. The audio survives;
   * this line is how the user learns it stopped. It is a prop rather than local state so it does
   * not depend on this component's mount, which the break's navigations can end at any time.
   */
  readonly entryNotice?: string | null;
  /**
   * B-35/§21.5/R-7 — report whether the **finished card** is the step on screen.
   *
   * The workflow's step is the only place this fact lives, and the strip that consumes it is three
   * levels above, in the chrome — so it is reported up the same shell↔hub channel B-31 built for
   * `workflowOpen`, rather than re-derived from the card row (which would answer *"a final card
   * exists"*, a different question: the same row also renders as *reference* at the top of the
   * full-time flow, and a `final` row is what makes this component OPEN on the card, not what keeps
   * it there).
   *
   * Optional for the same reason `entryNotice` is: the workflow has two hosts and only one of them
   * has a notification strip. The break hub renders it inside the takeover chrome; the match record
   * (§18/B-18, at full time) renders it in the tab world, where there is no strip to withhold
   * anything from. A host with nothing to tell is not made to say so.
   */
  readonly onCardShowing?: (showing: boolean) => void;
}

/**
 * The default {@link AdjustmentsWorkflowBProps.onCardShowing}. Declared at module scope so its
 * identity is stable across renders — a fresh arrow in the destructuring default would change the
 * reporting effect's deps on every render.
 */
const REPORT_NOTHING = (): void => {};

/** The Add/Edit sheet's target: an existing entry, or `null` for a new manual adjustment. */
type EditorTarget = { readonly entry: AdjustmentEntry | null };

export function AdjustmentsWorkflowB({
  match,
  context,
  onClose,
  entryNotice = null,
  onCardShowing = REPORT_NOTHING,
}: AdjustmentsWorkflowBProps) {
  const { state, actions } = useWorkflowB(match, context);
  const [editor, setEditor] = useState<EditorTarget | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);

  const byId = useMemo(() => {
    const map = new Map<string, WorkflowEntry>();
    for (const entry of state.entries) map.set(entry.hypothesis.id, entry);
    return map;
  }, [state.entries]);

  /**
   * B-16 — the same patterns the "because —" lines are built from here, in the shape the card
   * reads them. Derived from the workflow's own `entries` rather than re-read: the card the user
   * is looking at must cite the counts the screen behind it just showed, and a second read at a
   * second instant is how those two come to disagree.
   */
  const cardSources: readonly CoachCardSource[] = useMemo(
    () =>
      state.entries.map((entry) => ({
        id: entry.hypothesis.id,
        text: entry.hypothesis.text,
        phase: entry.hypothesis.phase,
        count: entry.hypothesis.cachedCount,
        annotations: entry.annotations.map((note) => note.text),
      })),
    [state.entries],
  );

  /**
   * B-35/R-7 — the report. The cleanup is not tidiness: this component is unmounted by every one
   * of the break's navigations (flags review, the lock review, the second-half whistle, a kill),
   * and a flag left `true` through any of them would withhold the update banner for the rest of
   * the match with nothing on screen to explain it. Reporting `false` on the way out makes the
   * suppression last exactly as long as the card does — the workflow re-reports on remount, which
   * is how BH-1's return to the card restores it.
   */
  useEffect(() => {
    onCardShowing(state.step === 'done');
    return () => {
      onCardShowing(false);
    };
  }, [state.step, onCardShowing]);

  const card = state.card;
  const selected = card?.selectedHypothesisIds ?? [];
  const selectedSet = new Set(selected);
  const selectedEntries = state.entries.filter((entry) => selectedSet.has(entry.hypothesis.id));
  const renderable = renderableCountB(card);
  const reference = referenceCardB(context, state.halftimeCard);
  const detail = detailId === null ? null : byId.get(detailId);
  /**
   * B-18 — where 'Back' goes, in the words of the surface the user came from. The workflow has
   * two doors since this unit: the break hub at halftime and the match record at full time, and a
   * full-time flow that offered 'Back to the break' would name a moment that is over.
   */
  const backLabel =
    context === ARTIFACT_PHASE.fullTime ? 'Back to the match record' : 'Back to the break';
  /**
   * B-22 — what the request will actually do with the team profile, from the same rules
   * `buildCoachRequestB` applies. The disclosure's wording and the empty-profile note are both
   * this one value, so the sentence the user reads cannot drift from the payload that leaves.
   *
   * §18.5 — the count and the freshness stamp ride the `'sending'` line, so the two-player gap a
   * forgotten `active` toggle leaves is readable at the one moment it can still be fixed.
   */
  const rosterDisclosure = rosterDisclosureB(match.purpose, context, state.roster, state.shape);
  const profileFacts = teamProfileFacts(state.roster, state.shape);

  if (state.phase === 'loading') {
    return (
      <div className="wfb" data-testid="wfb" data-step="loading">
        <p className="wfb-empty">Opening the adjustments…</p>
      </div>
    );
  }

  if (state.phase === 'failed') {
    return (
      <div className="wfb" data-testid="wfb" data-step="failed">
        <p className="wfb-empty" role="alert" data-testid="wfb-failed">
          {state.notice ?? 'The adjustments could not be opened. The log has the detail.'}
        </p>
        <button type="button" className="wfb-secondary" data-testid="wfb-close" onClick={onClose}>
          {backLabel}
        </button>
      </div>
    );
  }

  return (
    <div
      className="wfb"
      data-testid="wfb"
      data-step={state.step}
      data-context={context}
      data-selected={String(selected.length)}
      data-entries={String(card?.adjustments.length ?? 0)}
      data-renderable={String(renderable)}
      data-failure={state.failure ?? 'none'}
      role="region"
      aria-label="Adjustments"
    >
      <header className="wfb-bar">
        <h2 className="wfb-title">{TITLE[state.step]}</h2>
        <div className="wfb-bar__actions">
          {/* §2.5's draft exists from the moment the workflow opens, so its one-tap resolution
              has to exist from that moment too — on every step, not only the last one. F-1's
              completion guard points here, and a peek the user cannot undo would strand it. */}
          {state.step !== 'done' && card !== null && card.status === 'draft' && (
            <button
              type="button"
              className="wfb-linkish"
              data-testid="wfb-discard"
              disabled={state.busy}
              onClick={() => {
                actions.discard(onClose);
              }}
            >
              Discard
            </button>
          )}
          <button type="button" className="wfb-secondary" data-testid="wfb-close" onClick={onClose}>
            Back
          </button>
        </div>
      </header>

      {/* §21.4 — the entry action's own line. Separate from `wfb-notice` below and deliberately
          NOT dismissible: that one reports something the user just tried and can retry, this one
          reports something the app did to a recording on their behalf. */}
      {entryNotice !== null && (
        <p className="wfb-entrynotice" role="status" data-testid="wfb-entry-notice">
          {entryNotice}
        </p>
      )}

      {state.notice !== null && (
        <p className="wfb-notice" role="status" data-testid="wfb-notice">
          {state.notice}
          <button type="button" className="wfb-linkish" onClick={actions.dismissNotice}>
            Dismiss
          </button>
        </p>
      )}

      {/* §2.5 step 4 — the halftime card, as reference context at the top of the FULL-TIME flow
          (draft or final: at full time the break's draft may still be unfinished, and the half's
          thinking is context either way). `referenceCardB` is what keeps this off the halftime
          flow, where the same row is the card being built. Reachable once B-18 opens the workflow
          at `fullTime` from the match record — this unit owns the card surface, that one owns the
          door. */}
      {state.step !== 'done' && reference !== null && (
        <section className="wfb-reference" data-testid="wfb-reference">
          <p className="wfb-lead">From the break:</p>
          <CoachCardView
            card={reference}
            purpose={match.purpose}
            sources={cardSources}
            roster={state.roster}
            testId="cardb-reference"
          />
        </section>
      )}

      {/* ── Step 1 — Select (scenarios 22–23) ──────────────────────────────── */}
      {state.step === 'select' && (
        <>
          <p className="wfb-lead">
            Everything you marked, most-seen first. Pick as many as you like — or none, and just
            write your own.
          </p>

          {state.entries.length === 0 ? (
            <p className="wfb-empty" data-testid="wfb-nothing-observed">
              Nothing was recorded yet, so there is nothing to send. You can still write the
              adjustments yourself.
            </p>
          ) : (
            <>
              <div className="wfb-row">
                <button
                  type="button"
                  className="wfb-secondary"
                  data-testid="wfb-select-all"
                  disabled={state.busy}
                  onClick={() => {
                    actions.selectAll(true);
                  }}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="wfb-secondary"
                  data-testid="wfb-select-none"
                  disabled={state.busy}
                  onClick={() => {
                    actions.selectAll(false);
                  }}
                >
                  Select none
                </button>
              </div>

              <ul className="wfb-picks" data-testid="wfb-picks">
                {state.entries.map((entry) => {
                  const on = selectedSet.has(entry.hypothesis.id);
                  return (
                    <li className="wfb-pick" key={entry.hypothesis.id} data-selected={on ? 'true' : 'false'}>
                      <button
                        type="button"
                        className="wfb-pick__main"
                        role="checkbox"
                        aria-checked={on}
                        data-testid={`wfb-pick-${entry.hypothesis.id}`}
                        disabled={state.busy}
                        onClick={() => {
                          actions.toggleSelection(entry.hypothesis.id);
                        }}
                      >
                        <span className="wfb-pick__box" aria-hidden="true">
                          {on ? '✓' : ''}
                        </span>
                        <span className="wfb-pick__count">{entry.hypothesis.cachedCount}</span>
                        <span className="wfb-pick__body">
                          <span className="wfb-pick__text">{entry.hypothesis.text}</span>
                          {entry.annotations.length > 0 && (
                            <span
                              className="wfb-pick__notes"
                              data-testid={`wfb-notes-${entry.hypothesis.id}`}
                            >
                              {entry.annotations.map((note) => note.text).join(' · ')}
                            </span>
                          )}
                        </span>
                        <PhaseIcon phase={entry.hypothesis.phase} />
                      </button>
                      <button
                        type="button"
                        className="wfb-linkish"
                        data-testid={`wfb-annotate-${entry.hypothesis.id}`}
                        onClick={() => {
                          setDetailId(entry.hypothesis.id);
                        }}
                      >
                        Annotate
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <div className="wfb-footer">
            <button
              type="button"
              className="wfb-primary"
              data-testid="wfb-ask"
              disabled={state.busy || selected.length === 0}
              onClick={actions.generate}
            >
              Ask Coach Claude ({selected.length})
            </button>
            <button
              type="button"
              className="wfb-secondary"
              data-testid="wfb-write-own"
              onClick={actions.goToReview}
            >
              Write my own
            </button>
          </div>
          {/* B-22 — the missing input, said plainly, with the place to fix it.
              Below the footer so nothing above the send button moves when it appears (a control a
              thumb returns to must not relocate as a side effect of state), and ABOVE the
              disclosure because position is preattentive and this outranks a standing sentence.
              It never blocks: an empty roster is legal and the generate still runs. */}
          {rosterDisclosure === 'empty' && (
            <p className="wfb-roster-gap" data-testid="wfb-roster-empty">
              {ROSTER_EMPTY_NOTE}
            </p>
          )}
          {/* §5.6's disclosure, under the button that does the sending — rendered every time
              rather than once, because a sentence about where a child's description goes is
              not a tooltip the user should have to remember seeing. Its wording is now decided
              from what the request will actually carry (`rosterDisclosureB`), so it cannot claim
              to send team descriptions on a device that has none. */}
          <p
            className="wfb-disclosure"
            data-testid="wfb-disclosure"
            data-roster={rosterDisclosure}
          >
            {/* `Date.now()` inline, per B-1: the stamp prints its year only when the profile was
                last changed in another calendar year, and today is what decides that. Re-read on
                every render is correct and free — the answer changes once a year. */}
            {DISCLOSURE_COPY[rosterDisclosure](profileFacts, Date.now())}
          </p>
        </>
      )}

      {/* ── Step 2 — Generate (scenarios 24, 27, 28) ───────────────────────── */}
      {state.step === 'generate' && (
        <div className="wfb-generate" data-testid="wfb-generating">
          <p className="wfb-generate__line">Asking Coach Claude…</p>
          <p className="wfb-generate__elapsed" data-testid="wfb-elapsed">
            {state.elapsedS}s
          </p>
          <p className="wfb-lead">
            It stops itself after {Math.round(config.COACH_TIMEOUT_MS / 1000)} seconds. You never
            have to wait for it.
          </p>
          <button
            type="button"
            className="wfb-secondary"
            data-testid="wfb-skip"
            onClick={actions.skipToWriting}
          >
            Skip to writing
          </button>
        </div>
      )}

      {/* ── Step 3 — Review (scenarios 25–26) + the finalize ───────────────── */}
      {state.step === 'review' && (
        <>
          {state.failure !== null && (
            <section className="wfb-failure" role="alert" data-testid={`wfb-failure-${state.failure}`}>
              <p className="wfb-failure__lead">{FAILURE_COPY[state.failure].lead}</p>
              <p className="wfb-failure__then">{FAILURE_COPY[state.failure].then}</p>
              <div className="wfb-row">
                {FAILURE_COPY[state.failure].retry && (
                  <button
                    type="button"
                    className="wfb-secondary"
                    data-testid="wfb-retry"
                    disabled={state.busy || selected.length === 0}
                    onClick={actions.generate}
                  >
                    Try Coach Claude again
                  </button>
                )}
                <button
                  type="button"
                  className="wfb-primary"
                  data-testid="wfb-failure-write"
                  onClick={() => {
                    setEditor({ entry: null });
                  }}
                >
                  Write an adjustment
                </button>
              </div>
              {FAILURE_COPY[state.failure].credentialHint && (
                <p className="wfb-failure__hint" data-testid="wfb-credential-hint">
                  The service credential lives in More → service credential.
                </p>
              )}
            </section>
          )}

          {(card?.adjustments.length ?? 0) === 0 ? (
            <p className="wfb-empty" data-testid="wfb-no-entries">
              Nothing here yet. Write the adjustments you want to cover — one per point you will
              make.
            </p>
          ) : (
            <ul className="wfb-entries" data-testid="wfb-entries">
              {(card?.adjustments ?? []).map((entry) => (
                <li
                  className="wfb-entry"
                  key={entry.entryId}
                  data-testid={`wfb-entry-${entry.entryId}`}
                  data-status={entry.status}
                  data-origin={entry.origin}
                  data-edited={entry.aiOriginalText === null ? 'false' : 'true'}
                >
                  <p className="wfb-entry__text">{entry.text}</p>
                  <div className="wfb-entry__meta">
                    <span className="wfb-entry__phase">
                      <PhaseIcon phase={entry.phase} />
                      {phaseLabelB(entry.phase)}
                    </span>
                    <span className="wfb-entry__origin">
                      {entry.origin === 'ai' ? 'Coach Claude' : 'Yours'}
                    </span>
                    <span className="wfb-entry__status">{STATUS_WORD[entry.status]}</span>
                  </div>
                  {entry.sourceHypothesisIds.length > 0 && (
                    <p className="wfb-entry__because" data-testid={`wfb-because-${entry.entryId}`}>
                      <span className="wfb-entry__becauselabel">because —</span>{' '}
                      {entry.sourceHypothesisIds
                        .map((id) => {
                          const source = byId.get(id);
                          return source === undefined
                            ? null
                            : `${source.hypothesis.text} (${String(source.hypothesis.cachedCount)})`;
                        })
                        .filter((line): line is string => line !== null)
                        .join('; ')}
                    </p>
                  )}
                  <div className="wfb-entry__actions">
                    {entry.status !== 'accepted' && (
                      <button
                        type="button"
                        className="wfb-primary"
                        data-testid={`wfb-accept-${entry.entryId}`}
                        disabled={state.busy}
                        onClick={() => {
                          actions.setEntryStatus(entry, 'accepted');
                        }}
                      >
                        Accept
                      </button>
                    )}
                    <button
                      type="button"
                      className="wfb-secondary"
                      data-testid={`wfb-edit-${entry.entryId}`}
                      onClick={() => {
                        setEditor({ entry });
                      }}
                    >
                      Edit
                    </button>
                    {entry.status !== 'rejected' ? (
                      <button
                        type="button"
                        className="wfb-secondary"
                        data-testid={`wfb-reject-${entry.entryId}`}
                        disabled={state.busy}
                        onClick={() => {
                          actions.setEntryStatus(entry, 'rejected');
                        }}
                      >
                        Reject
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="wfb-secondary"
                        data-testid={`wfb-unreject-${entry.entryId}`}
                        disabled={state.busy}
                        onClick={() => {
                          actions.setEntryStatus(entry, 'suggested');
                        }}
                      >
                        Put it back
                      </button>
                    )}
                    {entry.origin === 'manual' && (
                      <button
                        type="button"
                        className="wfb-linkish"
                        data-testid={`wfb-remove-${entry.entryId}`}
                        disabled={state.busy}
                        onClick={() => {
                          actions.removeEntry(entry.entryId);
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="wfb-row">
            <button
              type="button"
              className="wfb-secondary"
              data-testid="wfb-add"
              disabled={!canAddEntryB(card, config.COACH_CARD_MAX_ENTRIES)}
              onClick={() => {
                setEditor({ entry: null });
              }}
            >
              Add my own
            </button>
            <button
              type="button"
              className="wfb-linkish"
              data-testid="wfb-back-to-select"
              onClick={actions.goToSelect}
            >
              Back to what you saw
            </button>
          </div>
          {!canAddEntryB(card, config.COACH_CARD_MAX_ENTRIES) && (
            <p className="wfb-lead" data-testid="wfb-at-cap">
              A card holds {config.COACH_CARD_MAX_ENTRIES} adjustments. Remove one to add another.
            </p>
          )}

          <div className="wfb-footer">
            <button
              type="button"
              className="wfb-primary"
              data-testid="wfb-finalize"
              disabled={state.busy || renderable === 0}
              onClick={actions.finalize}
            >
              Make the card ({renderable})
            </button>
          </div>
        </>
      )}

      {/* ── Step 4 — the card (B-16; scenarios 29–32) ──────────────────────── */}
      {state.step === 'done' && (
        <div className="wfb-done" data-testid="wfb-done">
          {/* The card names itself and counts itself, so this line says the one thing it does not:
              that it is now permanent. (It used to repeat the count, which put "4 adjustments" on
              screen three times over — caught reading the B-16 screenshots.) */}
          <p className="wfb-done__line">It stays with the match and never changes.</p>
          {/* The card is never null on this step (both routes to it read the row first). The
              guard is here so a re-read that somehow came back empty costs the card and not the
              way out — a dead-ended break surface is worse than a missing card. */}
          {card !== null && (
            <CoachCardView
              card={card}
              purpose={match.purpose}
              sources={cardSources}
              roster={state.roster}
              testId="cardb"
            />
          )}
          <button type="button" className="wfb-primary" data-testid="wfb-done-back" onClick={onClose}>
            {backLabel}
          </button>
        </div>
      )}

      {/* Both sheets are NON-modal (see the header): the whistle stays live around them. */}
      {editor !== null && (
        <AdjustmentSheetB
          entry={editor.entry}
          selectable={selectedEntries}
          onSave={(draft) => {
            if (editor.entry === null) actions.addManual(draft);
            else actions.saveEdit(editor.entry, draft);
            setEditor(null);
          }}
          onClose={() => {
            setEditor(null);
          }}
        />
      )}

      {detail !== undefined && detail !== null && (
        <HypothesisSheetB
          match={match}
          hypothesis={detail.hypothesis}
          onChanged={actions.refresh}
          onClose={() => {
            setDetailId(null);
            actions.refresh();
          }}
        />
      )}
    </div>
  );
}

const TITLE: Record<'select' | 'generate' | 'review' | 'done', string> = {
  select: 'What did you see?',
  generate: 'Asking Coach Claude',
  review: 'The adjustments',
  done: 'Card made',
};

const STATUS_WORD: Record<AdjustmentEntry['status'], string> = {
  suggested: 'Suggested',
  accepted: 'On the card',
  rejected: 'Rejected',
};
